import { bus, type Events } from '~/services/bus.ts'

type EventCallback<T extends keyof Events> = (params: Events[T]) => void

/**
 * Bus composable for use in the Vue lifecycle. Handles cleanup automatically.
 */
export function useBus() {
  let listeners: { event: keyof Events; callback: EventCallback<any> }[] = []

  /**
   * Register an event listener.
   *
   * Returns a cleanup function to remove this specific listener.
   */
  function on<T extends keyof Events>(event: T, callback: EventCallback<T>) {
    listeners.push({ event, callback })
    bus.on(event, callback)

    return () => off(event, callback)
  }

  /**
   * Remove an event listener.
   *
   * If no callback is provided, removes all listeners for the event.
   */
  function off<T extends keyof Events>(event: T, callback?: EventCallback<T>) {
    if (callback) {
      findAndSpliceArray(listeners, (listener) => listener.event === event && listener.callback === callback)
      bus.off(event, callback)
    } else {
      for (const index in listeners) {
        const listener = listeners[index]
        if (listener.event === event) {
          listeners.splice(Number(index), 1)
        }
      }
      bus.off(event)
    }
  }

  /**
   * Call the callback only once.
   *
   * Returns a cleanup function to cancel the listener before it fires.
   */
  function once<T extends keyof Events>(event: T, callback: EventCallback<T>) {
    const wrappedCallback = (params: Events[T]) => {
      callback(params)
      off(event, wrappedCallback as EventCallback<T>)
    }

    return on(event, wrappedCallback as EventCallback<T>)
  }

  /**
   * Call the callback until it returns truthy.
   *
   * Returns a cleanup function to cancel the listener.
   */
  function until<T extends keyof Events>(event: T, callback: (params: Events[T]) => unknown) {
    const wrappedCallback = (params: Events[T]) => {
      if (callback(params)) {
        off(event, wrappedCallback as EventCallback<T>)
      }
    }

    return on(event, wrappedCallback as EventCallback<T>)
  }

  // Automatically cleanup all listeners on unmount.
  onBeforeUnmount(() => {
    for (const { event, callback } of listeners) {
      bus.off(event, callback)
    }
    listeners = []
  })

  return { emit: bus.emit, off, on, once, until, listeners }
}
